import { Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ScrollDetail } from '@ionic/angular';
import { IonContent } from '@ionic/angular/standalone';
import { ButtonComponent } from 'src/app/components/primitives/button/button.component';
import { PanelComponent } from 'src/app/components/scaffolds/panel/panel.component';
import { ScriptButtonComponent } from 'src/app/components/elements/script-button/script-button.component';
import { CategoryButtonSmallComponent } from 'src/app/components/elements/category-button-small/category-button-small.component';
import { SlidesComponent } from 'src/app/components/elements/slides/slides.component';
import { StateService } from 'src/app/services/state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ErrorService } from 'src/app/services/error.service';
import { AddressDetailsComponent } from 'src/app/components/elements/address-details/address-details.component';
import { DeliveryMethodOptionComponent } from 'src/app/components/elements/delivery-method-option/delivery-method-option.component';
import { UiService } from 'src/app/services/ui.service';
import { Router } from '@angular/router';
import { OrderWrapperComponent } from 'src/app/components/elements/order-wrapper/order-wrapper.component';
import { AuthService } from 'src/app/services/auth.service';
import { Order } from '@chemist2u/types-client/C2U/ParseObjects';
import { Subscription } from 'rxjs';
import { ProductScrollerComponent } from 'src/app/components/scaffolds/product-scroller/product-scroller.component';
import { AmplitudeService } from 'src/app/services/amplitude.service';
import { FabComponent } from "../../components/primitives/fab/fab.component";
import { ProgramWrapperComponent } from "../../components/elements/program-wrapper/program-wrapper.component";
import { NativeService } from 'src/app/services/native.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.page.html',
    styleUrls: ['./home.page.scss'],
    standalone: true,
    imports: [
      CommonModule,
      IonContent,
      FormsModule,
      ReactiveFormsModule,
      ButtonComponent,
      PanelComponent,
      ScriptButtonComponent,
      CategoryButtonSmallComponent,
      SlidesComponent,
      ProductScrollerComponent,
      AddressDetailsComponent,
      DeliveryMethodOptionComponent,
      OrderWrapperComponent,
      FabComponent,
      ProgramWrapperComponent
    ]
})
export class HomePage implements OnInit, OnDestroy {
  @ViewChild('contentWrapper', { static: true }) contentWrapperRef!: IonContent;
  
  private router = inject(Router);
  private $auth = inject(AuthService);
  private $state = inject(StateService);
  private $native = inject(NativeService);
  private $ui = inject(UiService);
  private $error = inject(ErrorService);
  private $amplitude = inject(AmplitudeService);

  public isLoggedIn = this.$state.isLoggedIn;
  public isWeb = this.$native.isWeb;
  public hasAddress = this.$state.hasAddress;

  private orders = signal<Order[]>([]);
  private session = toSignal(this.$state.bSession);
  private pharmacy = toSignal(this.$state.bAllocatedPharmacy);

  public deliveryMethod = toSignal(this.$state.bSelectedFulfillmentMethod)
  public categories = toSignal(this.$state.bCategories);
  public activeOrders = signal<Order[]>([]);
  public activeProgramOrders = signal<Order[]>([]);
  public hasActiveOrders = signal<boolean>(false);
  public hasActiveProgramOrders = signal<boolean>(false);
  public hasPrograms = signal<boolean>(false);
  public showToTop = signal<boolean>(false);

  public placeDetails = computed(() => this.session()?.address);

  private ordersSub?: Subscription;
  private subscriptionsSub?: Subscription;

  ngOnInit() {
    this.$amplitude.track('NAVIGATE_HOME');
    this.ordersSub = this.$state.bOrders.subscribe(orders => {
      this.orders.set(orders);
      this.setHasActiveOrders();
    });
    this.subscriptionsSub = this.$state.bSubscriptions.subscribe(subs => {
      this.setHasPrograms();
    });
  }

  ngOnDestroy() {
    this.ordersSub?.unsubscribe();
    this.subscriptionsSub?.unsubscribe();
  }

  setHasActiveOrders() {
    const activeOrders = this.orders()?.filter((order) => order.status !== "Delivered" && order.status !== "Cancelled" && order.status !== "Completed");
    this.activeOrders.set(activeOrders);
    this.hasActiveOrders.set(activeOrders.length > 0);
    const activeProgramOrders = activeOrders.filter((order) => order.type == "subscription");
    this.activeProgramOrders.set(activeProgramOrders);
    this.hasActiveProgramOrders.set(activeProgramOrders.length > 0);
  }

  setHasPrograms() {
    const val = this.$state.bSubscriptions.getValue().filter(s => s.status == "Active").length > 0;
    this.hasPrograms.set(val);
  }

  public pharmacyAddress = computed(() => {
    if(!this.pharmacy()) return undefined;
    return this.pharmacy()?.attributes;
  });

  editAddress() {
    this.$ui.setAddress.next(this.session()?.address);
  }

  editDeliveryNotes() {
    this.$ui.setDeliveryNotes.next(this.session()!.address!);
  }

  setDeliveryMethod() {
    this.$ui.setDeliveryMethod.next(this.session()!.address!);
  }

  viewAllCategories() {
    this.router.navigate(['/tabs/browse']);
  }

  goToCategory(catId: string) {
    this.router.navigate([`/tabs/browse/medicine-results/${catId}`], {
      queryParams: { type: 'category' }
    });
    this.$amplitude.track('NAVIGATE_CATEGORY', { categoryId: catId });
  }

  async signUp() {
    this.$amplitude.track('ANON_SIGNUP');
    this.logIn();
  }

  async logIn() {
    try {
      await this.$auth.showLogin();
    } catch (error) {
      this.$error.showToast({
        message: "Error logging in with biometrics",
        header: "Error",
        position: "top",
        duration: 2000,
      });
    }
  }

  addScript() {
    this.$amplitude.track('ADD_SCRIPT_CTA', {
      origin: "HomeScreen",
      content: "Prescriptions",
      UI: "PrimaryCTA_Button",
    });
    if (!this.isLoggedIn()) {
      this.$ui.showLogin.next();
    } else if (!this.hasAddress()) {
      this.$ui.setAddress.next(this.session()?.address);
    } else {
      this.router.navigate(['/add-script']);
    }
  }

  onScroll(ev: CustomEvent<ScrollDetail>) {
    const scrollTop = ev.detail.scrollTop;
    if (scrollTop > 150) {
      this.showToTop.set(true);
    } else if (this.showToTop() && scrollTop < 10) {
      this.showToTop.set(false);
    }
  }

  goToTop() {
    this.contentWrapperRef.scrollToTop(300);
  }
}